
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/useAuth';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { computeAdjustedEstimateTotal, fetchBaseCostMap } from '@/utils/estimateTotalCalculation';
import { useImpersonationGuard } from '@/hooks/useImpersonationGuard';

export const useInvoiceCreation = (estimateId: string | null) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isReadOnly } = useImpersonationGuard();

  return useMutation({
    mutationFn: async ({ values, estimate, lineItems, selectedLineItems, lineItemPercentages, existingInvoices }: any) => {
      if (isReadOnly) throw new Error('Cannot create invoices in View Only mode');
      if (!estimateId || !estimate || !lineItems || !user?.id) {
        throw new Error('Missing required data');
      }

      // Fetch company VAT registration status
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('is_vat_registered')
        .eq('id', estimate.company_id)
        .single();

      if (companyError) {
        console.error('Error fetching company VAT status:', companyError);
        throw companyError;
      }

      const isVatRegistered = companyData?.is_vat_registered || false;

      // Fetch base costs for adjustment calculation
      const adjustmentMetadata = estimate.adjustment_metadata || null;
      const baseCostMap = adjustmentMetadata?.filters?.length > 0
        ? await fetchBaseCostMap(lineItems)
        : undefined;

      // Calculate remaining balance for 'full' invoice type using centralized utility
      const { adjustedSubtotal: estimateTotal } = computeAdjustedEstimateTotal(
        lineItems,
        adjustmentMetadata,
        baseCostMap
      );
      const totalAlreadyInvoiced = (existingInvoices || []).reduce(
        (sum: number, inv: any) => sum + (inv.total_amount || 0), 0
      );
      const remainingBalance = estimateTotal - totalAlreadyInvoiced;

      // Calculate totals based on invoice type
      let lineItemsTotal = 0;
      let itemsToInvoice = lineItems;
      
      if (values.invoice_type === 'partial' && selectedLineItems?.length > 0) {
        itemsToInvoice = lineItems.filter((item: any) => 
          selectedLineItems.includes(item.id)
        );
      }

      // Calculate the base total for selected items using centralized utility
      const { adjustedSubtotal: baseTotal } = computeAdjustedEstimateTotal(
        itemsToInvoice.map((item: any) => {
          // Apply line item percentage if it's a partial invoice
          if (values.invoice_type === 'partial' && lineItemPercentages?.[item.id]) {
            const itemCost = item.manual_override && item.manual_total_cost !== null
              ? item.manual_total_cost
              : item.total_cost_snapshot;
            return {
              ...item,
              // Override snapshot with percentage-applied value, skip further adjustment
              total_cost_snapshot: itemCost * lineItemPercentages[item.id] / 100,
              manual_override: false,
            };
          }
          return item;
        }),
        adjustmentMetadata,
        baseCostMap
      );

      // Apply calculation based on invoice type
      if (values.invoice_type === 'percentage' && values.percentage_of_estimate) {
        lineItemsTotal = estimateTotal * (values.percentage_of_estimate / 100);
      } else if (values.invoice_type === 'custom_amount' && values.custom_amount) {
        lineItemsTotal = values.custom_amount;
      } else if (values.invoice_type === 'full') {
        lineItemsTotal = remainingBalance;
      } else {
        lineItemsTotal = baseTotal;
      }

      let subtotal = lineItemsTotal;
      let taxAmount = 0;
      let totalAmount = lineItemsTotal;

      // Only apply tax calculations if company is VAT registered AND tax percentage > 0
      if (isVatRegistered && estimate.tax_percentage > 0) {
        if (estimate.tax_inclusive) {
          taxAmount = (lineItemsTotal * estimate.tax_percentage) / (100 + estimate.tax_percentage);
          subtotal = lineItemsTotal - taxAmount;
          totalAmount = lineItemsTotal;
        } else {
          subtotal = lineItemsTotal;
          taxAmount = (lineItemsTotal * estimate.tax_percentage) / 100;
          totalAmount = lineItemsTotal + taxAmount;
        }
      }

      // Create invoice with site address fields carried over from estimate
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          company_id: estimate.company_id,
          estimate_id: estimateId,
          title: values.title,
          description: values.description,
          customer_id: estimate.customer_id,
          created_by: user.id,
          due_date: values.due_date ? `${values.due_date.getFullYear()}-${String(values.due_date.getMonth() + 1).padStart(2, '0')}-${String(values.due_date.getDate()).padStart(2, '0')}` : null,
          payment_terms: values.payment_terms,
          special_instructions: values.special_instructions,
          tax_percentage: isVatRegistered ? estimate.tax_percentage : 0,
          tax_inclusive: estimate.tax_inclusive,
          subtotal: subtotal,
          tax_amount: taxAmount,
          total_amount: totalAmount,
          invoice_type: values.invoice_type || 'full',
          percentage_of_estimate: values.percentage_of_estimate,
          // Automatically carry over site address from estimate
          use_separate_site_address: estimate.use_separate_site_address,
          site_street_address: estimate.site_street_address,
          site_suburb: estimate.site_suburb,
          site_city: estimate.site_city,
          site_province_state: estimate.site_province_state,
           site_postal_code: estimate.site_postal_code,
          invoice_date: new Date().toISOString(),
        })
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // Create invoice line items
      const invoiceLineItems = itemsToInvoice.map((item: any, index: number) => {
        const quantityToInvoice = item.quantity_in_base_units;
        const isCustom = item.component_type === 'custom';

        // For custom items: snapshots store the FINAL (post-markup) price.
        // manual_unit_cost stores BASE cost (used by cost analysis), so we must NOT use it for invoice unit price.
        const totalCostToInvoice = isCustom
          ? (item.manual_total_cost ?? item.total_cost_snapshot)
          : (item.manual_override && item.manual_total_cost !== null
              ? item.manual_total_cost
              : item.total_cost_snapshot);

        const unitCostToInvoice = isCustom
          ? (quantityToInvoice > 0
              ? totalCostToInvoice / quantityToInvoice
              : (item.unit_cost_snapshot ?? 0))
          : (item.manual_override && item.manual_unit_cost !== null
              ? item.manual_unit_cost
              : item.unit_cost_snapshot);

        return {
          invoice_id: invoice.id,
          estimate_line_item_id: item.id,
          description: item.description,
          base_unit: item.base_unit,
          quantity_invoiced: quantityToInvoice,
          unit_cost: unitCostToInvoice,
          total_cost: totalCostToInvoice,
          sort_order: index,
          is_partial_invoice: values.invoice_type !== 'full',
        };
      });

      const { error: lineItemsError } = await supabase
        .from('invoice_line_items')
        .insert(invoiceLineItems);

      if (lineItemsError) throw lineItemsError;

      return invoice;
    },
    onSuccess: (invoice) => {
      toast.success('Invoice created successfully');
      queryClient.invalidateQueries({ queryKey: ['estimateInvoices', estimateId] });
      navigate(`/invoices/${invoice.id}`);
    },
    onError: (error) => {
      console.error('Error creating invoice:', error);
      toast.error('Failed to create invoice');
    },
  });
};
